import api from '@/features/api/axios';
import { CrmField } from '../types';
import { FormStructure } from '@/features/application/types/forms';
import { FieldMappingDetail } from '../types';

// For UI selection of CRM objects
export interface CrmObjectOption {
  id: string; // e.g., "Account", "MyCustomObject__c"
  label: string; // e.g., "Account", "My Custom Object"
}

export interface TestResult {
  formFieldLabel: string;
  crmFieldLabel: string;
  isValid: boolean;
  error?: string;
}

export interface SaveAndTestResult {
  validationPassed?: boolean;
  saved?: boolean;
  mappingId?: string;
  results: TestResult[];
  validationFailed?: boolean;
}

/**
 * Fetches supported CRM objects from the API
 */
export const fetchSupportedCrmObjects = async (): Promise<CrmObjectOption[]> => {
  const response = await api.get<CrmObjectOption[]>('/api/crm/objects');
  return response.data;
};

/**
 * Fetches CRM fields for a specific object type
 */
export const fetchCrmFieldsForObject = async (objectType: string): Promise<CrmField[]> => {
  const response = await api.get<{ fields: CrmField[] }>(`/api/crm/fields/${objectType}`);
  return response.data.fields;
};

/**
 * Fetches CRM object relationships for a specific object type
 */
export const fetchCrmObjectRelationships = async (
  objectType: string
): Promise<{ parents: string[]; children: string[] }> => {
  const response = await api.get<{ parents: string[]; children: string[] }>(
    `/api/crm/relationships/${objectType}`
  );
  return response.data;
};

/**
 * Fetches all available field mappings with their full details
 */
export const fetchAllMappingDetails = async (): Promise<FieldMappingDetail[]> => {
  const response = await api.get<FieldMappingDetail[]>(`/api/field-mapping/details`);
  return response.data;
};

/**
 * Fetches details for a specific field mapping
 */
export const fetchMappingDetails = async (mappingId: string): Promise<FieldMappingDetail> => {
  const response = await api.get<FieldMappingDetail>(`/api/field-mapping/${mappingId}`);
  return response.data;
};

/**
 * Deletes a field mapping
 */
export const deleteMapping = async (mappingId: string): Promise<void> => {
  await api.delete(`/api/field-mapping/${mappingId}`);
};

/**
 * Saves and tests field mappings in a single atomic operation.
 * The backend will first validate all fields. If validation passes, it proceeds to save.
 * If validation fails, it returns the test results without saving.
 */
export const saveAndTestFieldMappings = async (payload: {
  mappingId?: string;
  mappingName: string;
  primaryCrmObject: string;
  formStructure: FormStructure;
  isAddressPicklistOrgEnabled: boolean;
  allCrmFields: CrmField[];
}): Promise<SaveAndTestResult> => {
  const response = await api.post<SaveAndTestResult>('/api/field-mapping/save-and-test', payload);
  return response.data;
};

/**
 * Fetches the organization-wide setting for State & Country Picklists.
 */
export const fetchAddressPicklistSetting = async (): Promise<{
  isAddressPicklistOrgEnabled: boolean;
}> => {
  const response = await api.get<{ isAddressPicklistOrgEnabled: boolean }>(
    '/api/crm/org/address-picklist-setting'
  );
  return response.data;
};
